/**
 * CelsoCupom – Lógica compartilhada de cupom (PDP, cards, popup matrícula).
 * Usado por enroll-popup.js.liquid e card-gallery.liquid.
 */
(function (global) {
    'use strict';

    const CUPOM_SESSION_KEY = 'celso_coupon_data';
    const DISCOUNT_SETTINGS_KEY = '__celsoDiscountSettings';

    function safeJsonParse(raw, fallback) {
        if (!raw || typeof raw !== 'string') return fallback;
        try { return JSON.parse(raw); } catch (e) {
            // Tolerar JSON com vírgula à direita (erro comum em textarea de settings).
            try {
                var relaxed = raw.replace(/,\s*([}\]])/g, '$1');
                return JSON.parse(relaxed);
            } catch (e2) {
                return fallback;
            }
        }
    }

    function obterDadosCupomAtivoDaSessao() {
        if (typeof sessionStorage === 'undefined') return null;
        try {
            const raw = sessionStorage.getItem(CUPOM_SESSION_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            console.warn('CelsoCupom: não foi possível ler dados de cupom da sessão', e);
            return null;
        }
    }

    function normalizarCodigoCupom(valor) {
        if (valor === null || typeof valor === 'undefined') return '';
        let codigo = String(valor).trim().toUpperCase();
        if (typeof codigo.normalize === 'function') {
            codigo = codigo.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        }
        return codigo;
    }

    function codigosCupomCorrespondem(codigoA, codigoB) {
        const na = normalizarCodigoCupom(codigoA);
        const nb = normalizarCodigoCupom(codigoB);
        if (!na || !nb) return false;
        if (na === nb) return true;
        const baseA = na.split('-')[0] || na;
        const baseB = nb.split('-')[0] || nb;
        return baseA === baseB;
    }

    function codigoCupomEhCompleto(codigo) {
        var n = normalizarCodigoCupom(codigo);
        return n.includes('-');
    }

    function codigosCupomCorrespondemComRegra(codigoEntrada, codigoCandidato) {
        var entrada = normalizarCodigoCupom(codigoEntrada);
        var candidato = normalizarCodigoCupom(codigoCandidato);
        if (!entrada || !candidato) return false;
        // Candidato completo (schema/url com sufixos):
        // aceita match exato e também por base para cobrir cupom_json legado
        // que pode armazenar somente o código-base (sem sufixos).
        if (codigoCupomEhCompleto(candidato)) {
            if (entrada === candidato) return true;
            var baseEntrada = getCupomBase(entrada);
            var baseCandidato = getCupomBase(candidato);
            return Boolean(baseEntrada && baseCandidato && baseEntrada === baseCandidato);
        }
        // Candidato base: mantém compatibilidade por base.
        return codigosCupomCorrespondem(entrada, candidato);
    }

    function obterCupomJsonVariante(variant) {
        if (!variant || typeof variant !== 'object') return null;
        const raw = variant.cupom_json;
        if (!raw) return null;
        if (typeof raw === 'string') {
            try { return JSON.parse(raw); } catch (e) {
                console.warn('CelsoCupom: cupom_json inválido para variante', variant.id, e);
                return null;
            }
        }
        return raw;
    }

    function localizarCupomNoJson(cupomJson, couponCode) {
        if (!cupomJson) return null;
        const lista = Array.isArray(cupomJson.cupons) ? cupomJson.cupons : [];
        if (!lista.length) return null;
        if (couponCode) {
            const encontrado = lista.find(function (entry) {
                return codigosCupomCorrespondemComRegra(entry && entry.cupom, couponCode);
            });
            if (encontrado) return encontrado;
        }
        if (!couponCode && lista.length === 1) return lista[0];
        return null;
    }

    // Retorna TODAS as entradas que correspondem ao couponCode (base-only),
    // para evitar escolher "a primeira ocorrência" quando existem duplicados.
    function localizarCuponsNoJsonMatches(cupomJson, couponCode) {
        if (!cupomJson) return [];
        const lista = Array.isArray(cupomJson.cupons) ? cupomJson.cupons : [];
        if (!lista.length) return [];
        if (!couponCode) return [];
        return lista.filter(function (entry) {
            return codigosCupomCorrespondemComRegra(entry && entry.cupom, couponCode);
        });
    }

    function normalizarPorcentagemDemais(valor) {
        if (valor === null || typeof valor === 'undefined') return null;
        let numero = valor;
        if (typeof numero === 'string') numero = numero.replace(',', '.');
        const parsed = parseFloat(numero);
        if (!Number.isFinite(parsed) || parsed < 0) return null;
        let normalized = parsed > 1 ? parsed / 100 : parsed;
        if (!Number.isFinite(normalized)) return null;
        normalized = Math.min(Math.max(normalized, 0), 1);
        const ratio = 1 - normalized;
        if (ratio < 0) return null;
        const discountPercent = Math.round(normalized * 100 * 100) / 100;
        const restantePercent = Math.round(ratio * 100 * 100) / 100;
        return { ratio: ratio, discountPercent: discountPercent, restantePercent: restantePercent };
    }

    /** Para 1ª mensalidade: porcentagem no metafield (ex. 85% off) → ratio do valor a exibir (ex. 0.15). */
    function normalizarPorcentagemPrimeiraMensalidade(valor) {
        if (valor === null || typeof valor === 'undefined') return null;
        let numero = valor;
        if (typeof numero === 'string') numero = numero.replace(',', '.');
        const parsed = parseFloat(numero);
        if (!Number.isFinite(parsed)) return null;
        let normalized = parsed > 1 ? parsed / 100 : parsed;
        if (!Number.isFinite(normalized)) return null;
        normalized = Math.min(Math.max(normalized, 0), 1);
        const ratio = 1 - normalized;
        if (ratio < 0) return null;
        return { ratio: ratio };
    }

    function idsDeVarianteCorrespondem(candidateId, variantId) {
        if (!candidateId || variantId === undefined || variantId === null) return false;
        const variantIdStr = String(variantId);
        if (!variantIdStr) return false;
        if (String(candidateId) === variantIdStr) return true;
        const match = String(candidateId).match(/(\d+)$/);
        return match ? match[1] === variantIdStr : false;
    }

    function obterPercentualDemaisDaSessao(couponSessionData, variantId) {
        if (!couponSessionData || !couponSessionData.discount || !variantId) return null;
        const produtos = Array.isArray(couponSessionData.discount.products)
            ? couponSessionData.discount.products
            : [];
        for (var p = 0; p < produtos.length; p++) {
            var produto = produtos[p];
            if (!produto || !Array.isArray(produto.variants)) continue;
            for (var v = 0; v < produto.variants.length; v++) {
                var variant = produto.variants[v];
                if (variant && idsDeVarianteCorrespondem(variant.id, variantId)) {
                    if (typeof variant.nextPaymentPercentage === 'number' && variant.nextPaymentPercentage > 0) {
                        return variant.nextPaymentPercentage;
                    }
                }
            }
        }
        return null;
    }

    function obterPrecoBaseDaVariante(variant) {
        // Regra de negócio: o desconto é sempre calculado sobre o price atual da variante (preço de venda),
        // não sobre o compare_at_price (preço original riscado do Shopify).
        if (!variant) return 0;
        return Number(variant.price) || 0;
    }

    function getCupomFromUrl() {
        if (typeof global.window === 'undefined' || !global.window.location || !global.window.location.search) return '';
        var params = new URLSearchParams(global.window.location.search);
        return (params.get('cupom') || params.get('coupon') || params.get('discount') || params.get('code') || '').trim();
    }

    function getDescontosJsonFromSettings() {
        try {
            var container = global.window && global.window[DISCOUNT_SETTINGS_KEY];
            if (!container || typeof container !== 'object') return '';
            var raw = container.descontosJsonRaw;
            return typeof raw === 'string' ? raw : '';
        } catch (e) {
            return '';
        }
    }

    function getDiscountListFromSettings() {
        var raw = getDescontosJsonFromSettings();
        var parsed = safeJsonParse(raw, null);
        var list = [];

        // 1) Formato objeto esperado: { "discountList": ["CUPOM..."] }
        if (parsed && typeof parsed === 'object' && Array.isArray(parsed.discountList)) {
            list = parsed.discountList.slice();
        }
        // 2) Formato array JSON: ["CUPOM...","CUPOM2..."]
        else if (Array.isArray(parsed)) {
            list = parsed.slice();
        }
        // 3) Formato texto simples do textarea (um ou mais cupons, separados por vírgula/quebra de linha)
        else if (typeof raw === 'string' && raw.trim()) {
            list = raw
                .split(/[\n,;]+/g)
                .map(function (v) { return (v == null ? '' : String(v)).trim(); })
                .filter(function (v) { return Boolean(v); });
        }

        return list
            .map(function (v) { return (v == null ? '' : String(v)).trim(); })
            .filter(function (v) { return Boolean(v); });
    }

    function getCouponCandidates() {
        var urlCode = getCupomFromUrl();
        var settingsList = getDiscountListFromSettings();
        var combined = [];
        if (urlCode) combined.push(urlCode);
        if (Array.isArray(settingsList) && settingsList.length) {
            combined = combined.concat(settingsList);
        }
        if (!combined.length) return [];
        // De-dup preservando ordem (URL primeiro), para evitar candidatos repetidos.
        var seen = new Set();
        return combined.filter(function (code) {
            var normalized = normalizarCodigoCupom(code);
            if (!normalized) return false;
            if (seen.has(normalized)) return false;
            seen.add(normalized);
            return true;
        });
    }

    function getCupomBase(code) {
        var n = normalizarCodigoCupom(code);
        return (n.split('-')[0] || n).trim();
    }

    function parseCupomDateTimeParts(yyyymmdd, hhmm) {
        if (!/^\d{8}$/.test(yyyymmdd || '') || !/^\d{4}$/.test(hhmm || '')) return null;
        var year = Number(yyyymmdd.slice(0, 4));
        var month = Number(yyyymmdd.slice(4, 6));
        var day = Number(yyyymmdd.slice(6, 8));
        var hour = Number(hhmm.slice(0, 2));
        var minute = Number(hhmm.slice(2, 4));
        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
        if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
        if (month < 1 || month > 12) return null;
        if (day < 1 || day > 31) return null;
        if (hour < 0 || hour > 23) return null;
        if (minute < 0 || minute > 59) return null;
        var d = new Date(year, month - 1, day, hour, minute, 0, 0);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    }

    /**
     * Formatos esperados:
     * - BASE-TIPO-YYYYMMDD-HHmm-YYYYMMDD-HHmm
     * - BASE-TIPO-YYYYMMDD-YYYYMMDD (dia inteiro: 00:00 até 23:59)
     */
    function extrairJanelaValidadeDoCupom(code) {
        var n = normalizarCodigoCupom(code);
        if (!n) return null;
        var parts = n.split('-');
        if (parts.length < 4) return null;

        var startDate = '';
        var startTime = '0000';
        var endDate = '';
        var endTime = '2359';

        if (
            parts.length >= 6 &&
            /^\d{8}$/.test(parts[parts.length - 4] || '') &&
            /^\d{4}$/.test(parts[parts.length - 3] || '') &&
            /^\d{8}$/.test(parts[parts.length - 2] || '') &&
            /^\d{4}$/.test(parts[parts.length - 1] || '')
        ) {
            startDate = parts[parts.length - 4];
            startTime = parts[parts.length - 3];
            endDate = parts[parts.length - 2];
            endTime = parts[parts.length - 1];
        } else if (
            /^\d{8}$/.test(parts[parts.length - 2] || '') &&
            /^\d{8}$/.test(parts[parts.length - 1] || '')
        ) {
            startDate = parts[parts.length - 2];
            endDate = parts[parts.length - 1];
        } else {
            return null;
        }

        var inicio = parseCupomDateTimeParts(startDate, startTime);
        var fim = parseCupomDateTimeParts(endDate, endTime);
        if (!inicio || !fim) return null;
        return { inicio: inicio, fim: fim };
    }

    function cupomEstaExpirado(code, nowDate) {
        var janela = extrairJanelaValidadeDoCupom(code);
        // Sem janela parseável: tratar como "sem expiração no código".
        if (!janela || !janela.fim) return false;
        var now = nowDate instanceof Date ? nowDate : new Date();
        if (Number.isNaN(now.getTime())) return false;
        return now.getTime() > janela.fim.getTime();
    }

    function parseEntryDateTime(dateTimeStr) {
        if (typeof dateTimeStr !== 'string') return null;
        var raw = dateTimeStr.trim();
        if (!raw) return null;
        // Aceita formatos comuns: "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", com/sem timezone.
        var normalized = raw.replace(' ', 'T');
        var parsed = new Date(normalized);
        if (!Number.isNaN(parsed.getTime())) return parsed;
        return null;
    }

    function cupomEntryEstaExpirado(entry, nowDate) {
        if (!entry || typeof entry !== 'object') return false;
        var now = nowDate instanceof Date ? nowDate : new Date();
        if (Number.isNaN(now.getTime())) return false;

        var fim = parseEntryDateTime(entry.data_hora_fim);
        if (fim) {
            return now.getTime() > fim.getTime();
        }

        // Fallback legado para cupons com validade embutida no sufixo do código.
        return cupomEstaExpirado(entry.cupom, now);
    }

    // Compara se a janela de validade embutida no código do candidato (Shopify)
    // bate com a janela real da entry (cupom_json da variante). Necessário porque
    // codigosCupomCorrespondem/ComRegra só olham a base do código (ex.: "GS9776"),
    // então dois códigos Shopify distintos com a mesma base mas datas diferentes
    // (ex.: um duplicado/antigo) são indistinguíveis sem essa checagem extra.
    function candidatoBateComJanelaDaEntry(candidate, entry) {
        if (!entry || typeof entry !== 'object') return false;
        var janelaCandidato = extrairJanelaValidadeDoCupom(candidate);
        if (!janelaCandidato) return false;
        var entryInicio = parseEntryDateTime(entry.data_hora_inicio);
        var entryFim = parseEntryDateTime(entry.data_hora_fim);
        if (!entryInicio || !entryFim) return false;
        var mesmoDia = function (a, b) {
            return a.getFullYear() === b.getFullYear()
                && a.getMonth() === b.getMonth()
                && a.getDate() === b.getDate();
        };
        return mesmoDia(janelaCandidato.inicio, entryInicio) && mesmoDia(janelaCandidato.fim, entryFim);
    }

    function resolveBestCouponForVariant(variant, candidates, options) {
        if (!variant) return null;
        var cupomJson = obterCupomJsonVariante(variant);
        if (!cupomJson) return null;

        var lista = Array.isArray(cupomJson.cupons) ? cupomJson.cupons : [];
        if (!lista.length) return null;

        var best = null;
        var bestDiscount = -1;
        var bestCode = '';
        var bestWindowMatches = false;
        var candidatesList = Array.isArray(candidates) ? candidates : [];
        var EPS = 1e-9;

        // Cupom só entra com fonte explícita: URL (?cupom=) ou lista do schema (descontos_json / discountList).
        if (!candidatesList.length) {
            return null;
        }

        for (var i = 0; i < candidatesList.length; i++) {
            var candidate = candidatesList[i];
            if (!candidate) continue;
            if (cupomEstaExpirado(candidate)) continue;
            var matches = localizarCuponsNoJsonMatches(cupomJson, candidate);
            if (!matches || !matches.length) continue;

            for (var m = 0; m < matches.length; m++) {
                var entry = matches[m];
                if (!entry) continue;
                if (cupomEntryEstaExpirado(entry)) continue;

                var windowMatches = candidatoBateComJanelaDaEntry(candidate, entry);

                // Prioridade máxima: candidato cuja janela de validade bate com a
                // janela real da entry (data_hora_inicio/data_hora_fim). Isso evita
                // escolher um código Shopify "irmão" (mesma base, datas diferentes).
                if (windowMatches && !bestWindowMatches) {
                    bestWindowMatches = true;
                    bestDiscount = normalizarPorcentagemDemais(entry.porcentagem_demais)
                        ? normalizarPorcentagemDemais(entry.porcentagem_demais).discountPercent
                        : 0;
                    best = entry;
                    bestCode = escolherCodigoShopifyPreferido(entry, candidate);
                    continue;
                }
                if (bestWindowMatches && !windowMatches) {
                    // Já temos um candidato com janela batendo; não deixa um sem bater sobrescrever.
                    continue;
                }

                var normPct = normalizarPorcentagemDemais(entry.porcentagem_demais);
                var discountPercent = normPct ? normPct.discountPercent : 0;

                if (discountPercent > bestDiscount + EPS) {
                    bestDiscount = discountPercent;
                    best = entry;
                    bestCode = escolherCodigoShopifyPreferido(entry, candidate);
                    continue;
                }

                if (best && Math.abs(discountPercent - bestDiscount) <= EPS) {
                    var pBest = Number(best && best.porcentagem != null ? best.porcentagem : 0);
                    var pCandidate = Number(entry && entry.porcentagem != null ? entry.porcentagem : 0);
                    var preferredCode = escolherCodigoShopifyPreferido(entry, candidate);
                    if (Number.isFinite(pCandidate) && Number.isFinite(pBest) && pCandidate > pBest) {
                        best = entry;
                        bestCode = preferredCode;
                    } else if (preferredCode.length > String(bestCode || '').length) {
                        best = entry;
                        bestCode = preferredCode;
                    }
                }
            }
        }

        if (!best) return null;
        var resolvedCode = escolherCodigoShopifyPreferido(best, bestCode);
        var codeBase = getCupomBase(resolvedCode);
        return {
            code: String(resolvedCode || ''),
            displayCode: codeBase,
            entry: best,
            discountPercent: bestDiscount
        };
    }

    function escolherCodigoShopifyPreferido(entry, candidate) {
        var entryCode = entry && entry.cupom ? String(entry.cupom) : '';
        var candidateCode = candidate ? String(candidate) : '';
        if (entryCode && codigoCupomEhCompleto(entryCode)) {
            if (!candidateCode || codigosCupomCorrespondemComRegra(entryCode, candidateCode)) {
                if (!candidateCode || entryCode.length >= candidateCode.length) {
                    return entryCode;
                }
            }
        }
        return candidateCode || entryCode;
    }

    function variantIdInDiscountProducts(variantId, discountData) {
        if (!discountData || !discountData.discount || !Array.isArray(discountData.discount.products)) {
            return false;
        }
        for (var p = 0; p < discountData.discount.products.length; p++) {
            var produto = discountData.discount.products[p];
            if (!produto || !Array.isArray(produto.variants)) continue;
            for (var v = 0; v < produto.variants.length; v++) {
                var variant = produto.variants[v];
                if (variant && idsDeVarianteCorrespondem(variant.id, variantId)) {
                    return true;
                }
            }
        }
        return false;
    }

    var discountLookupCache = Object.create(null);

    async function fetchDiscountData(code, signal) {
        var normalized = normalizarCodigoCupom(code);
        if (!normalized) return null;
        if (discountLookupCache[normalized]) {
            return discountLookupCache[normalized];
        }
        try {
            var response = await fetch('/apps/discount?code=' + encodeURIComponent(normalized), { signal: signal });
            if (!response.ok) return null;
            var data = await response.json();
            if (data && data.success) {
                discountLookupCache[normalized] = data;
                return data;
            }
        } catch (error) {
            if (error && error.name === 'AbortError') throw error;
        }
        return null;
    }

    function filtrarCandidatosPorHint(candidates, hint) {
        var list = Array.isArray(candidates) ? candidates.slice() : getCouponCandidates().slice();
        if (!hint) return list;
        return list.filter(function (candidate) {
            return codigosCupomCorrespondem(candidate, hint);
        });
    }

    function ordenarCandidatosPorEspecificidade(candidates) {
        return candidates.slice().sort(function (a, b) {
            return String(b || '').length - String(a || '').length;
        });
    }

    async function resolveApplicableShopifyCodeForVariantId(variantId, candidates, options) {
        if (variantId === undefined || variantId === null) return null;
        options = options || {};
        var hint = options.hint ? String(options.hint).trim() : '';
        var pool = filtrarCandidatosPorHint(candidates, hint);
        if (!pool.length) {
            pool = getCouponCandidates().slice();
        }

        // Se houver candidatos cuja janela de validade bate com a entry real da
        // variante (data_hora_inicio/data_hora_fim), eles vão para o topo. Sem
        // isso, dois códigos Shopify com a mesma base (ex.: "GS9776") e datas
        // diferentes (um duplicado/antigo) empatam no critério de tamanho e o
        // desempate vira "qual está primeiro na lista de cupons do tema".
        var preferredSet = options.preferredCandidates && options.preferredCandidates.length
            ? new Set(options.preferredCandidates.map(normalizarCodigoCupom))
            : null;

        pool = pool.slice().sort(function (a, b) {
            if (preferredSet) {
                var aPreferred = preferredSet.has(normalizarCodigoCupom(a)) ? 0 : 1;
                var bPreferred = preferredSet.has(normalizarCodigoCupom(b)) ? 0 : 1;
                if (aPreferred !== bPreferred) return aPreferred - bPreferred;
            }
            return String(b || '').length - String(a || '').length;
        });

        for (var i = 0; i < pool.length; i++) {
            var candidate = pool[i];
            if (!candidate) continue;
            if (cupomEstaExpirado(candidate)) continue;
            var discountData = await fetchDiscountData(candidate, options.signal);
            if (!discountData) continue;
            if (variantIdInDiscountProducts(variantId, discountData)) {
                return String(discountData.code || candidate);
            }
        }

        return hint || null;
    }

    async function resolveApplicableShopifyCodeForVariant(variant, candidates, options) {
        if (!variant || variant.id === undefined || variant.id === null) return null;
        options = options || {};
        var cupomJson = obterCupomJsonVariante(variant);
        var pool = Array.isArray(candidates) ? candidates.slice() : getCouponCandidates().slice();
        var matching = pool.filter(function (candidate) {
            return candidate && localizarCupomNoJson(cupomJson, candidate);
        });
        if (!matching.length && options.hint) {
            matching = filtrarCandidatosPorHint(pool, options.hint);
        }
        if (!matching.length) {
            var implicit = resolveImplicitCouponForVariant(variant);
            if (implicit && implicit.code) {
                matching = [implicit.code];
            }
        }

        // Entre os candidatos que batem pela base do código, marca como
        // preferenciais os que batem também pela janela real (data_hora_inicio/
        // data_hora_fim) das entries do cupom_json da variante.
        var entriesDaVariante = cupomJson && Array.isArray(cupomJson.cupons) ? cupomJson.cupons : [];
        var preferredCandidates = matching.filter(function (candidate) {
            return entriesDaVariante.some(function (entry) {
                return candidatoBateComJanelaDaEntry(candidate, entry);
            });
        });

        var mergedOptions = Object.assign({}, options, {
            preferredCandidates: preferredCandidates.length ? preferredCandidates : options.preferredCandidates
        });

        return resolveApplicableShopifyCodeForVariantId(
            variant.id,
            matching.length ? matching : pool,
            mergedOptions
        );
    }

    /**
     * Ratio de exibição do preço com cupom na PDP/cards.
     * Prioriza porcentagem_demais; se for 0, usa porcentagem (como o carrinho com final_price).
     */
    function resolverRatioPrecoExibicaoCupom(cupomEntry) {
        if (!cupomEntry) return null;
        var normDemais = normalizarPorcentagemDemais(cupomEntry.porcentagem_demais);
        if (normDemais && normDemais.discountPercent > 0 && normDemais.ratio > 0 && normDemais.ratio < 1) {
            return normDemais;
        }
        var normPrimeira = normalizarPorcentagemPrimeiraMensalidade(cupomEntry.porcentagem);
        if (normPrimeira && normPrimeira.ratio > 0 && normPrimeira.ratio < 1) {
            return normPrimeira;
        }
        return null;
    }

    /**
     * Ratio para preço parcelado em cards (12x): só porcentagem_demais.
     * Desconto da 1ª mensalidade não zera a parcela exibida no card.
     */
    function resolverRatioPrecoParcelasCupom(cupomEntry) {
        if (!cupomEntry) return null;
        var normDemais = normalizarPorcentagemDemais(cupomEntry.porcentagem_demais);
        if (normDemais && normDemais.discountPercent > 0 && normDemais.ratio > 0 && normDemais.ratio < 1) {
            return normDemais;
        }
        return null;
    }

    function obterDescontoExibicaoCupom(entry) {
        var ratioInfo = resolverRatioPrecoExibicaoCupom(entry);
        if (!ratioInfo || !(ratioInfo.ratio > 0)) return null;
        return {
            ratio: ratioInfo.ratio,
            discountPercent: ratioInfo.discountPercent != null
                ? ratioInfo.discountPercent
                : Math.round((1 - ratioInfo.ratio) * 100 * 100) / 100
        };
    }

    /**
     * Resolve cupom embutido no cupom_json da variante (sem URL/settings).
     * Usado na PDP/CTA quando o curso já possui cupom próprio, como no carrinho.
     */
    function resolveImplicitCouponForVariant(variant, options) {
        if (!variant) return null;
        options = options || {};
        var cupomJson = obterCupomJsonVariante(variant);
        var lista = cupomJson && Array.isArray(cupomJson.cupons) ? cupomJson.cupons : [];
        if (!lista.length) return null;

        var ativos = lista.filter(function (entry) {
            return entry && !cupomEntryEstaExpirado(entry) && obterDescontoExibicaoCupom(entry);
        });
        if (!ativos.length) return null;

        var best = null;
        var bestDesconto = -1;
        for (var i = 0; i < ativos.length; i++) {
            var entry = ativos[i];
            var desconto = obterDescontoExibicaoCupom(entry);
            if (!desconto) continue;
            if (!best || desconto.discountPercent > bestDesconto) {
                best = entry;
                bestDesconto = desconto.discountPercent;
            }
        }

        if (!best) return null;
        var code = best && best.cupom ? String(best.cupom) : '';
        return {
            code: code,
            displayCode: getCupomBase(code),
            entry: best,
            discountPercent: bestDesconto,
            implicit: true
        };
    }

    /**
     * Resolve cupom para exibição: candidatos explícitos (URL/settings) e, se permitido,
     * cupom implícito do cupom_json da variante.
     */
    function resolveEffectiveCouponForVariant(variant, options) {
        if (!variant) return null;
        options = options || {};
        var candidates = Array.isArray(options.candidates)
            ? options.candidates.slice()
            : getCouponCandidates().slice();
        var extraCandidates = Array.isArray(options.extraCandidates) ? options.extraCandidates : [];
        if (extraCandidates.length) {
            candidates = candidates.concat(extraCandidates);
        }

        var seen = new Set();
        candidates = candidates.filter(function (code) {
            var normalized = normalizarCodigoCupom(code);
            if (!normalized) return false;
            if (seen.has(normalized)) return false;
            seen.add(normalized);
            return true;
        });

        var resolved = resolveBestCouponForVariant(variant, candidates, options);
        if (resolved && resolved.entry) return resolved;

        if (options.allowImplicit === false) return null;
        return resolveImplicitCouponForVariant(variant, options);
    }

    /** Retorna { integer, decimals } em pt-BR (sem ponto nos milhares no integer). */
    function formatarPrecoIntegerDecimals(valorCentavos) {
        var reais = valorCentavos / 100;
        var formatted = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(reais);
        var parts = formatted.split(',');
        return {
            integer: (parts[0] || '0').replace(/\./g, ''),
            decimals: (parts[1] || '00').slice(0, 2)
        };
    }

    function formatarDinheiro(valorEmCentavos) {
        if (typeof global.Shopify !== 'undefined' && typeof global.Shopify.formatMoney === 'function') {
            return global.Shopify.formatMoney(valorEmCentavos, global.Shopify.money_format);
        }
        var currency = (global.window && global.window.Shopify && global.window.Shopify.currency && global.window.Shopify.currency.active) || 'BRL';
        return (valorEmCentavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: currency });
    }

    /**
     * Resolve valor da primeira mensalidade para uma variante/entry de cupom.
     * Mantém a mesma regra visual usada na shelf e no PDP para evitar discrepância com o carrinho.
     * @param {*} variant
     * @param {*} cupomEntry
     * @returns {object|null} { valorCentavos, valorFormatado }
     */
    function resolverValorPrimeiraMensalidade(variant, cupomEntry) {
        if (!variant || !cupomEntry) return null;
        var normPrimeira = normalizarPorcentagemPrimeiraMensalidade(cupomEntry.porcentagem);
        if (!normPrimeira) return null;
        var basePrimeiraCents = obterPrecoBaseDaVariante(variant);
        if (!Number.isFinite(basePrimeiraCents) || basePrimeiraCents <= 0) return null;
        var valorCentavos = Math.round(basePrimeiraCents * normPrimeira.ratio);
        if (!Number.isFinite(valorCentavos) || valorCentavos < 0) return null;
        return {
            valorCentavos: valorCentavos,
            valorFormatado: formatarDinheiro(valorCentavos)
        };
    }

    /**
     * Resolve valor das demais mensalidades (para propriedade do carrinho).
     * @param {*} variant - variante com cupom_json, price, compare_at_price
     * @param {string} couponCode - código do cupom
     * @param {object} couponSessionData - dados da sessão (celso_coupon_data)
     * @returns {object|null} { valorCentavos, percentual, percentualRestante, valorFormatado }
     */
    function resolverValorDemaisMensalidades(variant, couponCode, couponSessionData) {
        if (!variant) return null;
        var cupomJson = obterCupomJsonVariante(variant);
        var cupomAtivo = localizarCupomNoJson(cupomJson, couponCode);
        var percentualMetafield = cupomAtivo && cupomAtivo.porcentagem_demais;
        var percentualSessao = obterPercentualDemaisDaSessao(couponSessionData, variant.id);
        var percentualNormalizado = normalizarPorcentagemDemais(percentualMetafield) || normalizarPorcentagemDemais(percentualSessao);
        if (!percentualNormalizado) return null;
        // Regra de negócio: demais mensalidades sempre calculadas sobre o price da variante.
        // Se porcentagem_demais = 0, mantém o valor cheio (sem desconto).
        var precoBase = Number(variant && variant['price']) || 0;
        if (!Number.isFinite(precoBase) || precoBase <= 0) return null;
        var valorCentavos = Math.round(precoBase * percentualNormalizado.ratio);
        if (!Number.isFinite(valorCentavos) || valorCentavos < 0) return null;
        return {
            valorCentavos: valorCentavos,
            percentual: percentualNormalizado.discountPercent,
            percentualRestante: percentualNormalizado.restantePercent,
            valorFormatado: formatarDinheiro(valorCentavos)
        };
    }

    var CelsoCupom = {
        CUPOM_SESSION_KEY: CUPOM_SESSION_KEY,
        DISCOUNT_SETTINGS_KEY: DISCOUNT_SETTINGS_KEY,
        obterDadosCupomAtivoDaSessao: obterDadosCupomAtivoDaSessao,
        normalizarCodigoCupom: normalizarCodigoCupom,
        codigosCupomCorrespondem: codigosCupomCorrespondem,
        obterCupomJsonVariante: obterCupomJsonVariante,
        localizarCupomNoJson: localizarCupomNoJson,
        normalizarPorcentagemDemais: normalizarPorcentagemDemais,
        normalizarPorcentagemPrimeiraMensalidade: normalizarPorcentagemPrimeiraMensalidade,
        idsDeVarianteCorrespondem: idsDeVarianteCorrespondem,
        obterPercentualDemaisDaSessao: obterPercentualDemaisDaSessao,
        obterPrecoBaseDaVariante: obterPrecoBaseDaVariante,
        getCupomFromUrl: getCupomFromUrl,
        getDiscountListFromSettings: getDiscountListFromSettings,
        getCouponCandidates: getCouponCandidates,
        getCupomBase: getCupomBase,
        extrairJanelaValidadeDoCupom: extrairJanelaValidadeDoCupom,
        cupomEstaExpirado: cupomEstaExpirado,
        resolveBestCouponForVariant: resolveBestCouponForVariant,
        resolveImplicitCouponForVariant: resolveImplicitCouponForVariant,
        resolveEffectiveCouponForVariant: resolveEffectiveCouponForVariant,
        resolveApplicableShopifyCodeForVariantId: resolveApplicableShopifyCodeForVariantId,
        resolveApplicableShopifyCodeForVariant: resolveApplicableShopifyCodeForVariant,
        variantIdInDiscountProducts: variantIdInDiscountProducts,
        escolherCodigoShopifyPreferido: escolherCodigoShopifyPreferido,
        formatarPrecoIntegerDecimals: formatarPrecoIntegerDecimals,
        formatarDinheiro: formatarDinheiro,
        resolverValorPrimeiraMensalidade: resolverValorPrimeiraMensalidade,
        resolverValorDemaisMensalidades: resolverValorDemaisMensalidades,
        resolverRatioPrecoExibicaoCupom: resolverRatioPrecoExibicaoCupom,
        resolverRatioPrecoParcelasCupom: resolverRatioPrecoParcelasCupom
    };

    if (typeof global !== 'undefined') {
        global.CelsoCupom = CelsoCupom;
    }
})(typeof window !== 'undefined' ? window : this);
